import {
  Reservation as ReservationType,
  ReservationGuest,
  Occassion as OccassionTypeFromShared,
  CustomDataValue,
} from 'types/shared/helper/reservations';
import {
  TicketDocument,
  WaitingListEntryDocument,
} from 'types/shared/documents';
import { AddOnOrder } from 'App/Experiences/types/addOnOrder';
import { Event, Ticket } from 'types/shared/documents/restaurants/event';

export enum ReservationStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  POSITIVE = 'positive',
  PENDING = 'pending',
  NEGATIVE = 'negative',
}

export enum CurrentStatus {
  NORMAL = 'normal',
  DONE = 'done',
  FAILED = 'failed',
  UPCOMING = 'upcoming',
  OVERDUE = 'overdue',
  SEATED = 'seated',
  HAS_ORDERED = 'hasOrdered',
  PAID = 'paid',
}

export type Guest = ReservationGuest & {
  guestId?: string;
  createNew?: boolean;
};

export type Reservation = ReservationType & {
  id: string;
  currentStatus: CurrentStatus; // Added by Context
  position?: number; // Added by Context
  startTimeInBlocks: number;
  endTimeInBlocks: number;
  reservationLength: number;
  warnings?: ({ id: string; message: string } | string)[];
  additional?: {
    type: 'event';
    eventId: string;
    amount: number;
    selectedOptions?: Record<string, number>;
    options: { id: string; internalName: string }[];
    voiceApi?: { source: string; test?: boolean };
  };
  previousTables?: string[] | null;
  noShowConfirmation?: {
    validTill: number;
    sendVia: 'sms' | 'email';
  };
  startedAt?: number;
  partlySeated?: boolean;
  ignoreWarnings?: Record<string, boolean>;
  group?: string;
  orders?: (AddOnOrder & { id: string })[];
  hasPayment?: boolean;
  ticket?: Ticket | null;
  addOnItems?: {
    id: string;
    line1: string;
    line2: string;
    price: number;
    refunded: boolean;
    payAtRestaurant?: boolean | undefined;
  }[];
  ticketData?: Record<string, number>;
  addOnData?: Record<string, number>;
  stammtischId?: string;
  regularTableId?: string;
  hasAddOnOrders?: boolean;
};

export type RegularTableRepeating =
  | {
      type: 'weekly';
      weekDays: number[];
    }
  | {
      type: 'monthly_fix_day';
      day: number;
    }
  | {
      type: 'yearly';
      date: string;
    }
  | {
      type: 'monthly';
      week: number;
      weekDay: number;
    };

export type RegularTable = {
  id: string;
  active: boolean;
  restaurant: string;
  guest: ReservationGuest;
  guests: number;
  firstReservationAt: string;
  lastReservation?: string;
  sendEmail?: boolean;
  time: string;
  reservationLength: number;
  tables: string[];
  space?: string;
  occassion?: string;
  xDaysInFuture?: number;
  repeating: RegularTableRepeating;
  createdAt: number;
  updatedAt: number;
  notes?: string;
};

export type LightReservation = Omit<Reservation, 'tables'> & {
  tableString: string;
};

export type Combination = {
  tables: string[];
  expression: string | false;
  priority: number;
  reservationLength: number;
  space: string;
  alias: string[];
};

export type TimeSlot = {
  block: number;
  blockedSlots: number;
  combinations: Combination[];
  eventId?: string | null;
  maxGuestsReached: boolean;
  time: string;
  reservationLength: number;
  shorterReservationLength?: boolean;
  totalSlots: number;
  waitinglist?: boolean;
};

export type EditReservation = {
  new?: boolean;
  keepTables?: boolean;
  clearValidTill?: boolean;
  commentEdited?: boolean;
  hostCommentEdited?: boolean;
  waitinglistEntry?: WaitingListEntryDocument;
  customData?: Record<string, CustomDataValue>;
  fixed?: boolean;
  date: string;
  timeSlot: null | TimeSlot;
  time?: string;
  space?: string;
  tables: null | string[];
  tableStr?: string | null;
  reservationLength?: number;
  reservationId?: string;
  occassion?: string;
  guests?: number;
  guest: null | (ReservationGuest & { createNew?: boolean });
  walkin?: boolean;
  page?: number;
  waiterSignature?: string;
  customId?: string;
  sendEmail?: boolean;
  clickCount?: number;
  lastTable?: string[] | null;
  sendNoShowSMS?: boolean;
  sendConfirmationEmail?: boolean;
  addToWaitinglist?: null | {
    time: string;
    flexibleTime?: null | { earliest: string; latest: string };
    preferredFormOfContact?: 'call' | 'whatsapp';
  };
  changedTablePerHandBefore?: boolean;
  originalUrl?: string;
  ticketId?: string | null;
  event?: Event | null;
  ticketMode?: 'reservation_only' | 'pay_at_restaurant' | 'payment_link';
  ticketOriginalAmount?: number;
  ticketOriginalCouponAmount?: number;
  ticketOriginalVoucherAmount?: number;
  ticket?: {
    options?: Record<string, number>;
    optionalAddOns?: Record<string, number>;
  };
  ticketCouponCode?: string;
  ticketCoupon?: {
    code: string;
    discount: {
      type: 'percentage' | 'amount';
      value: number;
    };
    per?: 'perGroup' | 'perPerson';
    title?: string;
    description?: string;
  } | null;
  ticketVoucherCode?: string;
  ticketVoucher?: {
    voucherId: string;
    restaurantId: string;
    message?: string;
    found: boolean;
    valid?: boolean;
    remainingAmount?: {
      value: string;
      currency: string;
    };
  } | null;
  ticketPartialPayment?: number;
  ticketPartialPaymentMode?: 'percentage' | 'amount';
  ticketPartialPaymentAmount?: number;
  ticketValidTillMinutes?: number;
  ticketValidTillValue?: number;
  ticketValidTillUnit?: 'minutes' | 'hours' | 'days';
  ticketSendVia?: 'email' | 'sms' | 'none';
  ticketCanEdit?: boolean;
};

export type Table = {
  id: string;
  name: string;
  coords: {
    direction: number;
    size: number;
    y: number;
    x: number;
  };
  space: string;
  windowTable?: boolean;
  occassions: string[];
  attributes?: string[];
  type: string;
  priority: number;
  expression: string;
  min: number;
  max: number;
  alias: string | null;
  /**
   * Shared-seating flag. When true, this physical table can host multiple
   * reservations concurrently as long as the sum of their guests ≤ max.
   */
  shared?: boolean;
};

export type Space = {
  id: string;
  name: string;
  description: string;
  outdoor?: boolean;
  offset: {
    x: number;
    y: number;
  };
  size: {
    height: number;
    width: number;
  };
};

export type Decoration = {
  id: string;
  coords: {
    x: number;
    y: number;
    size: number;
    direction: number;
    height?: number;
    width?: number;
  };
  space: string;
  text: string;
  type: string;
  color: string;
};

export type Wall = {
  id: string;
  coords: {
    x: number;
    y: number;
    direction: number;
    height: number;
    width: number;
  };
  space: string;
  type: string;
};

export type TableCombination = {
  id: string;
  primaryString: string;
  secondaryString: string;
  priority: number;
  occassions: string[];
  space: string;
  tables: string[];
  attributes?: string[];
  min: number;
  max: number;
  expression: boolean | string;
  alias: string[];
  /**
   * Derived: true iff every member table has `shared: true`.
   * Recomputed whenever combination membership changes.
   */
  shared?: boolean;
};

export type Background = {
  spaceId: string;
  floorPlanId?: string;
  imageUrl: string;
  imageUrlDM?: string;
  invert?: number;
  width: number;
  height: number;
  factor: number; // defaults to 10
  offset?: {
    x: number;
    y: number;
  };
};

export type Backgrounds = Record<string, Background>;

export type FloorPlan = {
  id: string;
  deleted?: boolean;
  name: string;
  description: string;
  icon: null | string;
  active: boolean;
  standard: boolean;
  sameDayMinGuestsTwo?: boolean;
  spaces: Space[];
  tables: Table[];
  tableCombinations: TableCombination[];
  decorations: Decoration[];
  walls: Wall[];
  updatedAt: number;
  publishedAt: number;
  backgrounds?: Backgrounds;
};

export type ResSettings = {
  hideResLength: boolean;
  hideOccassion: boolean;
};

export enum WaitingListStatus {
  PENDING = 'pending',
  NOTIFIED = 'notified',
  SEATED = 'seated',
  REMOVED = 'removed',
  CANCELED = 'canceled',
}

export type WaitingList = {
  id: string;
  restaurant: string;
  date: string;
  time: string;
  guests: number;
  occassion: string;
  guest: Guest;
  createdAt: number;
  status: WaitingListStatus;
  notified?: null | {
    timeStamp: number;
    time: string;
    occassion: string;
    tables?: string[];
    comment?: string;
    validTill?: number;
  };
};

export type Warning = {
  id: string;
  type: 'reservation';
  text?: string;
  cancelable?: boolean;
  tables: string[];
};

export type Occassion = OccassionTypeFromShared;

export interface DragTable extends Table {
  direction: number;
  updatedAt: number;
  style: {
    height: number;
    width: number;
    display: string;
    justifyContent: string;
    alignItems: string;
  };
  active: boolean;

  rotatable: boolean;
  position: {
    x: number;
    y: number;
    factor: number;
  };
  size: number;
  type: string;
  name: string;
  dots?: string[];
  y: number;
  x: number;
  blocked?: boolean;
}

export enum OccassionType {
  DEFAULT = 0,
  FOOD = 1, // Essen, Beides, Sushi, Abendessen,
  DRINKS_ONLY = 2, // Nur Getränke, Cocktails, zum trinken, ...
  TICKET = 3,
  BREAKFAST = 4,
  LUNCH = 5,
  BRUNCH = 6,
  DINNER = 7,
  LIVE_MUSIC = 8,
}
